import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  PiggyBank,
  TrendingUp,
  Landmark,
  Calculator,
  Receipt,
  Fuel,
  Cake,
  Scale,
  Percent,
  Coins,
  Search,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export interface CalculatorItem {
  id: string;
  title: string;
  description: string;
  url: string;
  category: "Loans" | "Investments" | "Utilities" | "Health & Life";
  icon: React.ElementType;
  keywords: string[];
}

export const ALL_CALCULATORS: CalculatorItem[] = [
  {
    id: "emi",
    title: "EMI Calculator",
    description: "Calculate monthly loan payments, home & car loans",
    url: "/",
    category: "Loans",
    icon: PiggyBank,
    keywords: ["emi", "loan", "home loan", "car loan", "bike loan", "personal loan", "mortgage", "interest", "monthly installment", "amortization"],
  },
  {
    id: "sip",
    title: "SIP Calculator",
    description: "Plan mutual fund systematic investments with step-up",
    url: "/sip-calculator",
    category: "Investments",
    icon: TrendingUp,
    keywords: ["sip", "mutual fund", "investment", "wealth", "step up", "returns", "growth", "lumpsum", "compound"],
  },
  {
    id: "fd",
    title: "FD Calculator",
    description: "Calculate fixed deposit interest & maturity amount",
    url: "/fd-calculator",
    category: "Investments",
    icon: Landmark,
    keywords: ["fd", "fixed deposit", "term deposit", "bank interest", "quarterly compounding", "maturity", "savings"],
  },
  {
    id: "compound-interest",
    title: "Compound Interest Calculator",
    description: "Calculate compound growth with daily, monthly & annual compounding",
    url: "/compound-interest-calculator",
    category: "Investments",
    icon: Coins,
    keywords: ["compound interest", "interest calculator", "growth", "apy", "cagr", "rule of 72", "wealth growth"],
  },
  {
    id: "gst",
    title: "GST Calculator",
    description: "Add or remove GST (5%, 12%, 18%, 28%) with CGST, SGST & IGST",
    url: "/gst-calculator",
    category: "Utilities",
    icon: Receipt,
    keywords: ["gst", "tax", "cgst", "sgst", "igst", "vat", "sales tax", "invoice", "18%", "5%", "12%", "28%"],
  },
  {
    id: "mileage",
    title: "Mileage Calculator",
    description: "Calculate fuel efficiency (km/l, mpg) and trip fuel cost",
    url: "/mileage-calculator",
    category: "Utilities",
    icon: Fuel,
    keywords: ["mileage", "fuel", "petrol", "diesel", "gas", "km/l", "mpg", "trip cost", "fuel economy", "distance"],
  },
  {
    id: "percentage",
    title: "Percentage Calculator",
    description: "Calculate percent increase, decrease, discount & fractions",
    url: "/percentage-calculator",
    category: "Utilities",
    icon: Percent,
    keywords: ["percentage", "percent", "discount", "percent increase", "percent decrease", "math", "fraction"],
  },
  {
    id: "general",
    title: "General & Scientific Calculator",
    description: "Everyday basic and scientific math operations",
    url: "/calculator",
    category: "Utilities",
    icon: Calculator,
    keywords: ["general", "calculator", "math", "scientific", "arithmetic", "sin", "cos", "log", "sqrt", "add"],
  },
  {
    id: "age",
    title: "Age Calculator",
    description: "Exact age in years/months/days, next birthday & zodiac",
    url: "/age-calculator",
    category: "Health & Life",
    icon: Cake,
    keywords: ["age", "birthday", "date of birth", "dob", "zodiac", "how old", "birthday countdown", "chronological age"],
  },
  {
    id: "bmi",
    title: "BMI Calculator",
    description: "Body Mass Index & ideal healthy weight range",
    url: "/bmi-calculator",
    category: "Health & Life",
    icon: Scale,
    keywords: ["bmi", "body mass index", "weight", "height", "health", "ideal weight", "obesity", "fitness"],
  },
];

interface CalculatorSearchDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CalculatorSearchDialog({ open, onOpenChange }: CalculatorSearchDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setIsOpen = isControlled ? (onOpenChange || (() => {})) : setInternalOpen;

  // Listen for Ctrl+K or Cmd+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [isOpen, setIsOpen]);

  const handleSelect = (url: string) => {
    navigate(url);
    setIsOpen(false);
  };

  const categories = ["Loans", "Investments", "Utilities", "Health & Life"] as const;

  return (
    <CommandDialog open={isOpen} onOpenChange={setIsOpen}>
      <CommandInput placeholder="Search any calculator... (e.g. loan, tax, fuel, compound)" />
      <CommandList className="max-h-[380px] p-2">
        <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
          No calculators found matching your search.
        </CommandEmpty>

        {categories.map((category) => {
          const items = ALL_CALCULATORS.filter((item) => item.category === category);
          if (items.length === 0) return null;

          return (
            <CommandGroup key={category} heading={category} className="mb-2">
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.id}
                    value={`${item.title} ${item.description} ${item.keywords.join(" ")}`}
                    onSelect={() => handleSelect(item.url)}
                    className="flex items-center justify-between rounded-xl px-3 py-2.5 cursor-pointer hover:bg-primary/10 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{item.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}

// Search Trigger Button for Header / Sidebar
export function SearchTriggerButton({ className = "", collapsed = false }: { className?: string; collapsed?: boolean }) {
  const [open, setOpen] = useState(false);

  if (collapsed) {
    return (
      <>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setOpen(true)}
          className={`h-9 w-9 rounded-xl border-border/60 hover:bg-primary/10 hover:text-primary ${className}`}
          aria-label="Search calculators"
        >
          <Search className="h-4 w-4" />
        </Button>
        <CalculatorSearchDialog open={open} onOpenChange={setOpen} />
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        type="button"
        className={`flex w-full items-center justify-between gap-2 rounded-xl border border-border/80 bg-muted/40 px-3 py-2 text-xs text-muted-foreground shadow-sm hover:border-primary/40 hover:bg-muted/70 transition-all ${className}`}
      >
        <div className="flex items-center gap-2">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <span>Search calculators...</span>
        </div>
        <kbd className="hidden sm:inline-flex h-5 items-center gap-0.5 rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </button>
      <CalculatorSearchDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
